/**
 * MarketplaceDocumentsSection
 *
 * Dynamic document display and upload for marketplace/collaboration pages.
 * Uses the same card pattern as DocumentsStep in lead wizard.
 *
 * Documents are resolved dynamically from master values in lead_data JSON,
 * using the same resolveDocuments() API as lead creation.
 *
 * Props:
 *   resolvedDocs       - Array of resolved document definitions
 *   groupedDocs        - Documents grouped by document_group_id
 *   loadingDocMapping  - Loading state for document definitions
 *   uploadedDocs       - Object { docDefId: [files] }
 *   uploadingDocs      - Object { docDefId: boolean }
 *   uploadProgress     - Object { docDefId: number }
 *   getUploadedFilesForDefinition - Function (docDefId) => files[]
 *   handleDocumentSelect - Function (file, docDefId) => void
 *   handleDocumentRemove - Function (uploadId, docDefId) => void
 *   canUpload          - Whether user can upload documents
 *   readOnly           - Whether to hide upload controls entirely
 */

import React from 'react';
import { Button } from 'primereact/button';
import { Tag } from 'primereact/tag';
import { Panel } from 'primereact/panel';
import { Message } from 'primereact/message';
import { FileUpload } from 'primereact/fileupload';
import { ProgressBar } from 'primereact/progressbar';
import { ProgressSpinner } from 'primereact/progressspinner';

const formatFileSize = (bytes) => {
    if (!bytes) return 'Unknown';
    const units = ['B', 'KB', 'MB', 'GB'];
    let i = 0;
    while (bytes >= 1024 && i < units.length - 1) {
        bytes /= 1024;
        i++;
    }
    return `${bytes.toFixed(bytes < 10 ? 1 : 0)} ${units[i]}`;
};

const MarketplaceDocumentsSection = ({
    resolvedDocs = [],
    groupedDocs = { groups: [], ungrouped: [] },
    loadingDocMapping = false,
    uploadedDocs = {},
    uploadingDocs = {},
    uploadProgress = {},
    getUploadedFilesForDefinition,
    handleDocumentSelect,
    handleDocumentRemove,
    canUpload = false,
    isOwner = false,
    currentProfileId = null,
    readOnly = false,
}) => {

    const getStatusConfig = (status) => {
        const configs = {
            pending: { label: 'Pending', severity: 'warning', icon: 'pi-clock' },
            uploaded: { label: 'Uploaded', severity: 'info', icon: 'pi-cloud-upload' },
            verified: { label: 'Verified', severity: 'success', icon: 'pi-check-circle' },
            rejected: { label: 'Rejected', severity: 'danger', icon: 'pi-times-circle' },
        };
        return configs[status] || configs.uploaded;
    };

    // Render single document card
    const renderDocumentCard = (item) => {
        const docDef = item.document_definition;
        if (!docDef) return null;

        const uploaded = getUploadedFilesForDefinition(docDef.id);
        const isUploading = uploadingDocs[docDef.id];
        const currentProgress = uploadProgress[docDef.id] || 0;
        const maxFiles = docDef.max_files || 5;
        // Always allow upload if canUpload — collaborators can add corrected docs
        // even if max_files reached (only owner can delete wrong ones)
        const canUploadMore = canUpload && !readOnly;

        return (
            <div key={item.id || docDef.id} className="surface-50 border-round p-2 mb-2">
                <div className="flex justify-content-between align-items-center mb-1">
                    <div className="flex align-items-center gap-2">
                        <i className={`${docDef.icon || 'pi pi-file'} text-sm`} />
                        <div>
                            <span className="font-semibold text-sm">{docDef.name}</span>
                            <div className="text-color-secondary text-xs">
                                Max: {docDef.max_file_size_mb || 10}MB | {maxFiles} files | {(docDef.allowed_formats || []).join(', ').toUpperCase()}
                            </div>
                        </div>
                    </div>
                    <div className="flex gap-1">
                        {item.is_required && <Tag value="Required" severity="danger" className="text-xs" />}
                        <Tag
                            value={`${uploaded.length}/${maxFiles}`}
                            severity={uploaded.length > 0 ? 'success' : 'warning'}
                            className="text-xs"
                        />
                    </div>
                </div>

                {/* Uploaded files list */}
                {uploaded.length > 0 && (
                    <div className="mb-1">
                        {uploaded.map(upload => {
                            const statusConfig = getStatusConfig(upload.status);
                            return (
                                <div key={upload.id} className="flex align-items-center justify-content-between p-1 surface-0 border-round mb-1 text-xs">
                                    <div className="flex align-items-center gap-1 flex-grow-1 overflow-hidden">
                                        <i className={`pi ${statusConfig.icon} text-xs text-${statusConfig.severity === 'info' ? 'blue' : statusConfig.severity === 'success' ? 'green' : statusConfig.severity === 'danger' ? 'red' : 'orange'}-500`} />
                                        <span className="text-xs white-space-nowrap overflow-hidden text-overflow-ellipsis">
                                            {upload.original_filename}
                                        </span>
                                        <span className="text-color-secondary" style={{ fontSize: '0.65rem' }}>
                                            ({formatFileSize(upload.file_size)})
                                        </span>
                                        <Tag value={statusConfig.label} severity={statusConfig.severity} className="text-xs" style={{ fontSize: '0.6rem', padding: '0 4px' }} />
                                        {upload.source === 'collaboration' && (
                                            <Tag value="Collab" severity="warning" className="text-xs" style={{ fontSize: '0.6rem', padding: '0 4px' }} />
                                        )}
                                    </div>
                                    <div className="flex gap-1">
                                        {upload.url && (
                                            <Button
                                                icon="pi pi-eye"
                                                className="p-button-rounded p-button-text p-button-sm p-button-info"
                                                style={{ width: '1.5rem', height: '1.5rem' }}
                                                onClick={() => window.open(upload.url, '_blank')}
                                                tooltip="View"
                                                tooltipOptions={{ position: 'top' }}
                                            />
                                        )}
                                        {(isOwner || (upload.source === 'collaboration' && currentProfileId && upload.uploader?.id === currentProfileId)) && (
                                            <Button
                                                icon="pi pi-trash"
                                                className="p-button-rounded p-button-text p-button-sm p-button-danger"
                                                style={{ width: '1.5rem', height: '1.5rem' }}
                                                onClick={() => handleDocumentRemove(upload.id, docDef.id)}
                                                tooltip="Delete"
                                                tooltipOptions={{ position: 'top' }}
                                            />
                                        )}
                                    </div>
                                </div>
                            );
                        })}
                    </div>
                )}

                {/* Upload button */}
                {canUploadMore && (
                    <FileUpload
                        mode="basic"
                        accept={(docDef.allowed_formats || ['pdf', 'jpg', 'jpeg', 'png']).map(f => `.${f}`).join(',')}
                        maxFileSize={(docDef.max_file_size_mb || 10) * 1024 * 1024}
                        chooseLabel={isUploading ? `Uploading ${currentProgress}%...` : 'Select File'}
                        chooseOptions={{ className: 'p-button-sm p-button-outlined w-full', icon: 'pi pi-upload' }}
                        className="mt-1"
                        auto
                        disabled={isUploading}
                        customUpload
                        uploadHandler={(e) => {
                            handleDocumentSelect(e.files[0], docDef.id);
                            e.options.clear();
                        }}
                    />
                )}
                {isUploading && <ProgressBar value={currentProgress} className="mt-1" style={{ height: '4px' }}  aria-label="Progress" />}
            </div>
        );
    };

    // Loading state
    if (loadingDocMapping) {
        return (
            <div className="flex align-items-center justify-content-center p-4">
                <ProgressSpinner style={{ width: '30px', height: '30px' }} />
                <span className="ml-2 text-sm text-color-secondary">Loading document requirements...</span>
            </div>
        );
    }

    // No document definitions found
    if (resolvedDocs.length === 0) {
        // Still show uploaded docs if any
        const totalUploaded = Object.values(uploadedDocs).flat().length;
        if (totalUploaded > 0) {
            return (
                <div>
                    <div className="flex align-items-center gap-2 mb-2">
                        <i className="pi pi-file text-primary text-sm" />
                        <span className="font-semibold text-sm">Uploaded Documents ({totalUploaded})</span>
                    </div>
                    {Object.entries(uploadedDocs).map(([defId, files]) => (
                        <div key={defId} className="surface-50 border-round p-2 mb-2">
                            <div className="font-semibold text-xs mb-1 text-color-secondary">
                                {files[0]?.document_definition?.name || 'Document'}
                            </div>
                            {files.map(file => {
                                const statusConfig = getStatusConfig(file.status);
                                return (
                                    <div key={file.id} className="flex align-items-center gap-1 p-1 surface-0 border-round mb-1 text-xs">
                                        <i className={`pi ${statusConfig.icon} text-xs`} />
                                        <span className="text-xs">{file.original_filename}</span>
                                        <span className="text-color-secondary" style={{ fontSize: '0.65rem' }}>({formatFileSize(file.file_size)})</span>
                                        <Tag value={statusConfig.label} severity={statusConfig.severity} style={{ fontSize: '0.6rem', padding: '0 4px' }} />
                                        {file.url && (
                                            <Button icon="pi pi-eye" className="p-button-rounded p-button-text p-button-sm p-button-info ml-auto" style={{ width: '1.5rem', height: '1.5rem' }} onClick={() => window.open(file.url, '_blank')} />
                                        )}
                                    </div>
                                );
                            })}
                        </div>
                    ))}
                </div>
            );
        }

        return (
            <Message severity="info" text="No document requirements found for this lead type." className="w-full" />
        );
    }

    // Render grouped documents
    return (
        <div>
            {/* Summary */}
            <div className="flex align-items-center justify-content-between mb-2">
                <div className="flex align-items-center gap-2">
                    <i className="pi pi-file text-primary text-sm" />
                    <span className="font-semibold text-sm">Documents</span>
                </div>
                <div className="flex gap-2">
                    <Tag
                        value={`${Object.values(uploadedDocs).flat().length} uploaded`}
                        severity={Object.values(uploadedDocs).flat().length > 0 ? 'success' : 'warning'}
                        className="text-xs"
                    />
                    <Tag
                        value={`${resolvedDocs.length} required`}
                        severity="info"
                        className="text-xs"
                    />
                </div>
            </div>

            {/* Grouped documents */}
            {groupedDocs.groups.map(group => (
                <Panel
                    key={group.id}
                    header={
                        <div className="flex align-items-center gap-2">
                            <i className="pi pi-folder text-sm" />
                            <span className="text-sm font-semibold">{group.name}</span>
                            <Tag value={group.docs.length} severity="info" className="text-xs" />
                        </div>
                    }
                    toggleable
                    collapsed={false}
                    className="mb-2"
                >
                    {group.docs.map(item => renderDocumentCard(item))}
                </Panel>
            ))}

            {/* Ungrouped documents */}
            {groupedDocs.ungrouped.length > 0 && (
                <Panel
                    header={
                        <div className="flex align-items-center gap-2">
                            <i className="pi pi-file text-sm" />
                            <span className="text-sm font-semibold">Other Documents</span>
                            <Tag value={groupedDocs.ungrouped.length} severity="info" className="text-xs" />
                        </div>
                    }
                    toggleable
                    collapsed={false}
                    className="mb-2"
                >
                    {groupedDocs.ungrouped.map(item => renderDocumentCard(item))}
                </Panel>
            )}
        </div>
    );
};

export default MarketplaceDocumentsSection;
